import { useState, useEffect, useRef } from "react"

/**
 * Arama inputları icin animasyonlu placeholder hook'u.
 * Verilen alan isimlerini sirayla harf harf yazar, bekler, siler ve sonrakine gecer.
 *
 * @param {string[]} fields - Donecek alan isimleri, orn: ["Firma adı", "E-posta", "Telefon"]
 * @param {object}   opts
 * @param {number}   opts.typingSpeed  - Harf yazma hizi (ms)  default 60
 * @param {number}   opts.deletingSpeed - Harf silme hizi (ms) default 35
 * @param {number}   opts.pauseAfterType - Yazdiktan sonra bekleme (ms) default 2000
 * @param {number}   opts.pauseAfterDelete - Sildikten sonra bekleme (ms) default 300
 * @param {string}   opts.prefix - Sabit on ek, orn: "Ara: " default ""
 * @returns {string} Anlik placeholder metni
 */
const useAnimatedPlaceholder = (fields = [], opts = {}) => {
  const {
    typingSpeed = 60,
    deletingSpeed = 35,
    pauseAfterType = 2000,
    pauseAfterDelete = 300,
    prefix = "",
  } = opts

  const [text, setText] = useState("")
  const indexRef = useRef(0)
  const charRef = useRef(0)
  const deletingRef = useRef(false)
  const timerRef = useRef(null)

  useEffect(() => {
    if (!fields.length) return

    const tick = () => {
      const current = fields[indexRef.current]
      if (!current) {
        indexRef.current = 0
        timerRef.current = setTimeout(tick, pauseAfterDelete)
        return
      }

      if (!deletingRef.current) {
        // Yazma
        charRef.current++
        setText(current.slice(0, charRef.current))

        if (charRef.current >= current.length) {
          // Tamamen yazildi, bekle sonra silmeye basla
          deletingRef.current = true
          timerRef.current = setTimeout(tick, pauseAfterType)
        } else {
          timerRef.current = setTimeout(tick, typingSpeed)
        }
      } else {
        // Silme
        charRef.current--
        setText(current.slice(0, charRef.current))

        if (charRef.current <= 0) {
          // Tamamen silindi, sonraki alana gec
          deletingRef.current = false
          indexRef.current = (indexRef.current + 1) % fields.length
          timerRef.current = setTimeout(tick, pauseAfterDelete)
        } else {
          timerRef.current = setTimeout(tick, deletingSpeed)
        }
      }
    }

    // Baslat
    indexRef.current = 0
    charRef.current = 0
    deletingRef.current = false
    setText("")
    timerRef.current = setTimeout(tick, pauseAfterDelete)

    return () => clearTimeout(timerRef.current)
  }, [fields.join(",")]) // eslint-disable-line

  return prefix + text + (text.length > 0 ? "..." : "")
}

/**
 * Her tablo icin backend'de aranan alanlarin Turkce karsiliklari.
 * import { SEARCH_FIELDS } from "hooks/useAnimatedPlaceholder"
 */
export const SEARCH_FIELDS = {
  users: [
    "Ad soyad",
    "E-posta",
    "Firma kodu",
    "Firma adı",
    "Cari kodu",
    "Cari adı",
  ],
  companies: [
    "Firma adı",
  ],
  accounts: [
    "Cari adı",
    "Cari kodu",
    "Unvan",
    "TC kimlik",
    "VKN",
    "Telefon",
    "Şehir",
    "Vergi dairesi",
  ],
  paymentLinks: [
    "Firma adı",
    "Cari adı",
    "Fatura firma adı",
    "Ödeme linki",
  ],
  registrations: [
    "Firma adı",
    "Yetkili adı",
    "E-posta",
  ],
  scheduledReports: [
    "E-posta adresi",
  ],
}

export default useAnimatedPlaceholder
